/**
 * REF Phase 2 (v2) — REF-specific types + constants. Shared wire types come
 * from {@link ../../tsm-app/models/tsm-app.model} via re-export, so REF code
 * imports from one place. See tms-ref-apps.md §5.2 for the reuse map.
 *
 * Jackson Rule #1: every variable typed. Jackson Rule #7: module-level
 * constants in SCREAMING_SNAKE_CASE.
 */

// ── Re-exports of shared wire-format types ───────────────────────────────────
// REF consumes the same DeltaResponse / EditPaneSection / etc. shapes the
// server emits for TSM. Re-exporting here keeps REF imports cohesive without
// duplicating type definitions.

export type {
	ActionItem,
	AppMetadata,
	DeltaEntry,
	DeltaResponse,
	EditPaneSection,
	EditPaneSubsection,
	EditPaneFieldsSubsection,
	EditPaneMatrixSubsection,
	EditPaneMenuEntTreeSubsection,
	EditPaneField,
	FieldState,
	LookupMatch,
	LookupSuggestResponse,
	MenuEntTreeNode,
	MenuEntTreeResponse,
	NavTarget,
	PaneActionButton,
	PaneButtonAction,
	TsmMode,
	UpdateBody,
} from '../../tsm-app/models/tsm-app.model';

export { IMPLEMENTED_MODES, isInsertLikeMode, lookupPickValue, pickValueFromMaps, paneVisibleInMode, visibleEntitledModes } from '../../tsm-app/models/tsm-app.model';

// Toolbar descriptor shapes originate in the filter-app model (shared across
// every TMS app family); re-exported here for the same one-import cohesion.
export type { ButtonDescriptor, ToolbarDescriptor } from '../../filter-app/models/filter-app.model';

// ── REF module constants ─────────────────────────────────────────────────────

import { TsmMode } from '../../tsm-app/models/tsm-app.model';

export const REF_ROUTE_PATH: string = 'ref/:appName';
export const REF_SELF_LOOKUP_PAGE_SIZE: number = 50;
export const DEFAULT_REF_MODE: TsmMode = 'inquire';

// ── REF-only interfaces ──────────────────────────────────────────────────────

/**
 * Result of a self-lookup row pick — the parent dispatches
 * {@code PickRefRecord(row, primaryKeyFields)} which builds this internally,
 * but the overlay component emits it for any direct subscribers.
 */
export interface RefSelfLookupPick {
	readonly primaryKeys: Record<string, string>;
	readonly values: ReadonlyArray<string>;
	/**
	 * The server's response-level {@code primaryKeyFields[]} — the SOURCE
	 * table's PK adaptors. The pill uses these to decide its label: when the
	 * set (minus tenant_cd) is empty, the PK map can't identify the record
	 * (ATI_TENANT binds its record key to the non-PK adaptor ati_tenant_cd;
	 * RateManager emits no PK snapshot at all) and the pill falls back to the
	 * picked row's first display value instead of joining the full load-key
	 * map (which now carries every legacy write-back field — names, dates).
	 */
	readonly keyFields: ReadonlyArray<string>;
}
